import { useMemo } from 'react';
import type {
  DealParticipantSummary,
  DealSourceType,
  DealThreadSummary,
  FileSourceChannel,
} from '@/types/deal.types';

/** Per-file projection that the intermediate documents grid renders.
 *  contractId is the engine's per-file pointer (lets the grid link each
 *  doc to /analysis/<contractId>); sourceChannel drives the per-doc
 *  channel badge. */
export interface IntermediateFile {
  fileId: string;
  fileName: string;
  contractId: string;
  fileType?: string;
  sizeBytes?: number | null;
  createdAt?: string;
  sourceChannel: FileSourceChannel;
}

/** Typed projection of the loose-cast extras the api-server's /deals/get
 *  hands back. Flattens the four Phase 3 surfaces (sourceType, threads,
 *  participants, files-with-source) into one shape so the intermediate
 *  sections can consume real types instead of re-casting at every read.
 *  Fields are optional because /deals/get pre-Phase-3 callers may still
 *  see responses that lack the new projections during rollout. */
/** Slice 2 — explicit/auto-picked Key Contract for the Deal Overview tile.
 *  isAutoPicked tells the UI whether to show "(suggested)" affordance vs.
 *  show the file as the user's locked-in choice. */
export interface KeyContractFile {
  fileId: string;
  fileName: string;
  contractId: string;
  fileType?: string;
  sizeBytes?: number | null;
  createdAt?: string;
  sourceChannel: FileSourceChannel;
  isAutoPicked: boolean;
}

/** Slice 3a — concrete next-step task pulled from linked-thread messages
 *  by the engine's `extractActionItems` AX program. Powers the right-rail
 *  Action Items panel. priority is one of 'urgent' | 'high' | 'medium' | 'low'. */
export interface IntermediateActionItem {
  description: string;
  priority: string;
  dueDate?: string | null;
  owner?: string | null;
  sourceMessageId?: string | null;
}

/** Slice 3a — one event in the deal's recent-activity timeline,
 *  synthesized from linked-thread messages by the engine's
 *  `summarizeRecentActivity` AX program. Newest-first. eventType is one of
 *  'message' | 'attachment' | 'milestone' | 'decision' | 'escalation'. */
export interface IntermediateActivityEvent {
  timestamp: string;
  eventType: string;
  summary: string;
  actor?: string | null;
  channel?: 'email' | 'imessage' | null;
}

export interface IntermediateDealData {
  sourceType: DealSourceType;
  threads: DealThreadSummary[];
  participants: DealParticipantSummary[];
  files: IntermediateFile[];
  linkedThreadCount: number;
  participantCount: number;
  primaryContractId: string | null;
  dealAnalysisId: string | null;
  analysisStatus: string | null;
  /** Slice 2 — the file the Deal Overview + Key Deal Terms panels hydrate
   *  against. Auto-picked (largest PDF) until user explicitly sets via
   *  /deals/setKeyContract. Null when no PDFs are attached. */
  keyContract: KeyContractFile | null;
  /** PDFs the user can pick between in the Core Document picker. The
   *  picker only renders when length ≥ 2. */
  keyContractCandidateFileIds: string[];
  /** Slice 3a — engine-derived action items from the linked threads.
   *  Empty array when the engine hasn't run thread-summary stages yet
   *  (no contract uploaded) or the deal has no linked threads. */
  actionItems: IntermediateActionItem[];
  /** Slice 3a — engine-derived recent-activity timeline. Same emptiness
   *  rules as actionItems. Newest-first. */
  activityEvents: IntermediateActivityEvent[];
}

const EMPTY: IntermediateDealData = {
  sourceType: 'upload',
  threads: [],
  participants: [],
  files: [],
  linkedThreadCount: 0,
  participantCount: 0,
  primaryContractId: null,
  dealAnalysisId: null,
  analysisStatus: null,
  keyContract: null,
  keyContractCandidateFileIds: [],
  actionItems: [],
  activityEvents: [],
};

export function useDealIntermediateData(
  apiDealData: unknown,
): IntermediateDealData {
  return useMemo(() => {
    if (!apiDealData) return EMPTY;
    const raw = apiDealData as {
      deal?: { sourceType?: DealSourceType };
      threads?: DealThreadSummary[];
      participants?: DealParticipantSummary[];
      files?: IntermediateFile[];
      latestAnalysis?: {
        dealAnalysisId?: string;
        status?: string;
        primaryContractId?: string;
        linkedThreadCount?: number;
        participantCount?: number;
      };
      keyContractFile?: KeyContractFile | null;
      keyContractCandidateFileIds?: string[];
      actionItems?: IntermediateActionItem[];
      activityEvents?: IntermediateActivityEvent[];
    };
    return {
      sourceType: raw.deal?.sourceType ?? 'upload',
      threads: raw.threads ?? [],
      participants: raw.participants ?? [],
      files: raw.files ?? [],
      linkedThreadCount: raw.latestAnalysis?.linkedThreadCount ?? 0,
      participantCount: raw.latestAnalysis?.participantCount ?? 0,
      primaryContractId: raw.latestAnalysis?.primaryContractId ?? null,
      dealAnalysisId: raw.latestAnalysis?.dealAnalysisId ?? null,
      analysisStatus: raw.latestAnalysis?.status ?? null,
      keyContract: raw.keyContractFile ?? null,
      keyContractCandidateFileIds: raw.keyContractCandidateFileIds ?? [],
      actionItems: raw.actionItems ?? [],
      activityEvents: raw.activityEvents ?? [],
    };
  }, [apiDealData]);
}
